'use client';
import styles from './styles.module.scss';
import {motion} from 'framer-motion';
import 'swiper/css/navigation';
import {useEffect, useRef, useState} from 'react';
import {isIOS} from 'react-device-detect';
import {
  handleLogin,
  loginState,
  handleUserInfo as loginUserInfo,
} from '@/utils/event/util';
import MyProfileImg from '@/components/my/common/item/MyProfileImg';
import globalDomains from '@/utils/global-domains';
import AutopayBenefitEventApi from '@/api/events/autopay-benefit';

const TARGET = 1; // 이벤트 대상_결제 전
const NONTARGET = 2; // 이벤트 비대상
const TARGET_BEFORE_PICK = 3; // 이벤트 대상_결제 후_혜택 선택 전
const TARGET_AFTER_PICK = 4; // 이벤트 대상_결제 후_혜택 선택 완료

const initialLayerData = {
  content: null,
  handleConfirm: null,
};

const AutopayBenefit = ({onBodyId}) => {
  const [loading, setLoading] = useState(false);
  const [activeGuideLines, setActiveGuideLines] = useState(-1);

  const [userNick, setUserNick] = useState('');
  const [userId, setUserId] = useState('');
  const [isEventActive, setIsEventActive] = useState();
  const [eventUserType, setEventUserType] = useState(); // 비로그인
  const [selectedBenefit, setSelectedBenefit] = useState();
  const [layerData, setLayerData] = useState(initialLayerData);
  const autopayCloseRef = useRef();

  const benefits = [
    {
      style: styles.benefit1,
      name: '스타벅스 아메리카노',
      id: 'coffee',
    },
    {
      style: styles.benefit2,
      name: '퀵뷰 30일 이용권',
      id: 'quickview',
    },
    {
      style: styles.benefit3,
      name: '티어1 구독 선물권 1개월',
      id: 'sub',
    },
  ];

  useEffect(() => {
    /**
     * 초기 로드시 body id 셋팅
     */
    onBodyId && onBodyId(styles.body);
    setLoading(true);

    // 팝업 닫힘 감지 타이머 클리어
    return () => {
      if (autopayCloseRef.current) {
        clearInterval(autopayCloseRef.current);
      }
    };
  }, [onBodyId]);

  const checkEventUser = async () => {
    const {
      result,
      eventUserType: userType,
      msg,
      isEventActive,
    } = await AutopayBenefitEventApi.autoPayEventInfo('checkEventUser');

    // 정상 응답
    if (result > 0) {
      setIsEventActive(isEventActive);
      setEventUserType(userType); // 이벤트 참여 대상

      // 이벤트 경품 선택 정보
      if (userType === TARGET_AFTER_PICK) {
        const {result, benefitType} =
          await AutopayBenefitEventApi.autoPayEventInfo('getBenefitData');
        if (result > 0) {
          setSelectedBenefit(benefitType); // 선택한 경품
        }
      }
    } else {
      return msg;
    }
  };

  // 로그인 정보 셋팅
  useEffect(() => {
    if (loginState) {
      const {userId, userNickname} = loginUserInfo();
      setUserNick(userNickname);
      setUserId(userId);

      checkEventUser();
    }
  }, [loginState]);

  const handleActiveGuideLine = (event) => {
    event.preventDefault();
    let activeNo =
      activeGuideLines === parseInt(event.currentTarget.value)
        ? -1
        : parseInt(event.currentTarget.value);

    setActiveGuideLines(activeNo);
  };

  const checkLoginWeb = () => {
    if (!loginState) {
      handleLogin();
      return;
    }

    // PC, 모웹에서만 이벤트 참여 가능
    if (window.afreeca.link.isWebview()) {
      setLayerData({
        content: 'PC, 모바일웹으로 참여 가능합니다.',
      });
      return;
    }

    return true;
  };

  // 대상자 확인하기 버튼
  const handleCheckEventTarget = async () => {
    if (!checkLoginWeb()) return;
    if (!eventUserType) {
      const errorMessage = await checkEventUser();
      if (errorMessage) {
        setLayerData({content: errorMessage});
        return;
      }
    }

    const layer = {content: ''};
    if (isEventActive) {
      if (eventUserType === NONTARGET) {
        layer.content = '이벤트 대상자가 아닙니다.';
      } else if (eventUserType === TARGET) {
        layer.content = (
          <>
            이벤트 참여 대상자입니다.
            <br />
            결제 페이지로 이동하시겠습니까?
          </>
        );
        layer.handleConfirm = () => {
          let url = `${globalDomains.POINT_AFREECATV}/SetBalloonAutoPayInfo.asp`;
          if (window.afreeca.link.isMobile()) {
            url += `?nDevice=${isIOS ? 1 : 2}&szWork=help&szKind=TBOX20131007000000&szUserId=${userId}&szVersion=1&szApp=mafreecatv&szModel=web&szOsVer=web&sys_type=webm&location=itemstore`;
          } else {
            url += `?sys_type=web&location=item`;
          }
          const autopayPopup = window.open(
            url,
            'autopayPopup',
            'top=0,left=0,width=435,height=530',
          );

          // 팝업이 닫힌 걸 감지하여 유저타입 다시 체크
          const timer = setInterval(() => {
            if (autopayPopup.closed) {
              clearInterval(timer);
              checkEventUser();
            }
          }, 500);
          autopayCloseRef.current = timer;
        };
      } else if (eventUserType === TARGET_BEFORE_PICK) {
        layer.content = (
          <>
            참여 완료되었습니다.
            <br />
            혜택을 선택해 주세요.
          </>
        );
        layer.handleConfirm = () => handleScrollTo('event01');
      } else if (eventUserType === TARGET_AFTER_PICK) {
        layer.content = '이벤트 참여 완료되었습니다.';
      }
    } else {
      layer.content = '이벤트가 종료되었습니다.';
    }

    setLayerData(layer);
  };

  /**
   * 경품 선택 api
   * @param benefitId coffee | quickview | sub
   */
  const pickBenefit = async (benefitId) => {
    const {result, msg} = await AutopayBenefitEventApi.pickBenefit(benefitId);

    const layer = {content: ''};
    // 정상 응답
    if (result > 0) {
      layer.content = (
        <>
          혜택 선택이 완료되었습니다.
          <span>
            구매한 별풍선을 모두 선물 한 유저 대상으로
            <br /> 혜택이 지급됩니다.
          </span>
        </>
      );
      await checkEventUser();
    } else {
      layer.content = msg;
    }
    setLayerData(layer);
  };

  // 선택하기 버튼
  const handleSetBenefit = (benefit) => () => {
    if (!checkLoginWeb()) return;

    const layer = {
      content: (
        <>
          {benefit.name}
          {benefit.id === 'coffee' ? '를' : '을'}
          <br /> 선택 하시겠습니까?
          <span>선택한 혜택은 변경이 불가합니다.</span>
        </>
      ),
      handleConfirm: () => pickBenefit(benefit.id),
    };
    setLayerData(layer);
  };

  const handleScrollTo = (targetId) => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  };

  const handleNavigateTo = (page) => () => {
    if (checkLoginWeb()) {
      window.open(`${globalDomains.ITEM_AFREECATV}/${page}.php`);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const event02 = document.getElementById('event02');
      const tab01 = document.querySelector(`.${styles.tab01}`);
      const tab02 = document.querySelector(`.${styles.tab02}`);

      if (event02) {
        const event02Top = event02.offsetTop;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const isWideScreen = window.innerWidth >= 721;

        if ((isWideScreen ? scrollTop / 0.75 : scrollTop) >= event02Top) {
          tab02.classList.add(styles.on);
          tab01.classList.remove(styles.on);
        } else {
          tab01.classList.add(styles.on);
          tab02.classList.remove(styles.on);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    loading && (
      <>
        <div id={styles.wrap}>
          <header className={styles.header}>
            <div className={styles.inner}>
              <h1>
                <a
                  href={`javascript:void(0)`}
                  onClick={() => {
                    /**
                     * 웹뷰 -> home 이동 스킴이 없어 리로드로 적용
                     */
                    if (window.afreeca.link.isWebview()) {
                      window.location.reload();
                      return;
                    }

                    window.location.href = `${globalDomains.WWW_AFREECATV}`;
                    return;
                  }}>
                  <img
                    src='//res.sooplive.co.kr/eventzone/event/2025/autoPay/logo.png'
                    alt='SOOP'
                  />
                </a>
              </h1>
              <div className={styles.user_info}>
                {loginState ? (
                  <>
                    <div className={styles.user}>
                      <MyProfileImg userId={userId} disableLazy={true} />
                      <span>{userNick}</span>
                    </div>
                    <button
                      type='button'
                      className={styles.account}
                      onClick={handleLogin}>
                      로그아웃
                    </button>
                  </>
                ) : (
                  <button
                    type='button'
                    className={styles.account}
                    onClick={handleLogin}>
                    로그인
                  </button>
                )}
              </div>
            </div>
          </header>
          <div className={styles.visual}>
            <div className={styles.motionBox}></div>
          </div>

          <div className={styles.stickeTab}>
            <div className={styles.inner}>
              <button
                type='button'
                className={`${styles.tab01} ${styles.on}`}
                onClick={() => handleScrollTo('event01')}>
                <span>EVENT 01</span>
                <p>
                  <strong>별풍선 자동충전</strong> 이벤트
                </p>
              </button>
              <button
                type='button'
                className={styles.tab02}
                onClick={() => handleScrollTo('event02')}>
                <span>EVENT 02</span>
                <p>
                  <strong>별풍선/퀵뷰/구독 자동결제</strong> 이벤트
                </p>
              </button>
            </div>
          </div>

          <div className={styles.eventWrap}>
            <div id='event01' className={styles.event01}>
              <div className={styles.event0101}>
                {benefits.map(({id, name, style}, idx) => (
                  <dl key={id} className={style}>
                    <dt>
                      혜택{idx + 1}-{name}
                    </dt>
                    <dd>
                      <button
                        type='button'
                        className={
                          // 이벤트가 종료된 경우
                          !isEventActive
                            ? styles.disabled
                            : // 이벤트 대상_결제 후_혜택 선택 전 유저
                              eventUserType === TARGET_BEFORE_PICK
                              ? ''
                              : // 이미 혜택을 선택한 경우
                                selectedBenefit === id
                                ? styles.end
                                : styles.disabled
                        }
                        onClick={
                          isEventActive && eventUserType === TARGET_BEFORE_PICK
                            ? handleSetBenefit({id, name})
                            : null
                        }>
                        {selectedBenefit === id ? '선택완료' : '선택하기'}
                      </button>
                    </dd>
                  </dl>
                ))}
                <button
                  type='button'
                  className={styles.link}
                  onClick={handleCheckEventTarget}>
                  대상자 확인하기
                </button>
              </div>
            </div>
            <div id='event02' className={styles.event02}>
              <button
                type='button'
                className={styles.link1}
                onClick={handleNavigateTo('starballoon')}>
                별풍선 자동결제
              </button>
              <button
                type='button'
                className={styles.link2}
                onClick={handleNavigateTo('quickview')}>
                퀵뷰 자동결제
              </button>
              <button
                type='button'
                className={styles.link3}
                onClick={handleNavigateTo('subscription')}>
                구독 자동결제
              </button>
            </div>
          </div>

          <section className={styles.section_6}>
            <div className={styles.inner}>
              <ul className={styles.noti_wrap}>
                <li
                  className={`${activeGuideLines === 0 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={0}>
                      <em>별풍선 자동 충전</em> 이벤트 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    animate={{
                      height: activeGuideLines === 0 ? 'auto' : 0,
                      opacity: activeGuideLines === 0 ? 1 : 0,
                    }}>
                    <ul>
                      {[
                        '본 이벤트는 별풍선 자동결제 대상 이벤트이며, 일반 별풍선 결제의 경우 이벤트 대상에서 제외됩니다.',
                        <>
                          SOOP에서 별풍선 자동결제 이력이 없는 경우, 3개월 내
                          자동결제 결제 이력이 없는 경우 이벤트 대상자로
                          적용됩니다.
                          <br />
                          (계정당 1회 제한)
                        </>,
                        '자동충전 등록 상태의 경우 혜택 선택이 불가하며, 결제 완료 시 혜택 선택이 가능합니다.',
                        <em key='3만원 이상 결제건'>
                          3만원 이상 결제건만 혜택 대상에 적용되며, 혜택 지급은
                          구매한 별풍선을 모두 선물 한 유저 대상으로 지급됩니다.
                        </em>,
                        '기준 금액 이상 결제 시 혜택 지급 대상이 되며, 혜택 선택 후 변경 불가능합니다.',
                        '비정상적이거나 불법적인 방법으로 이벤트에 참여하신 경우 대상에서 제외되며, 법적인 처벌을 받을 수 있습니다.',
                        '본 이벤트는 사정에 의하여 사전 고지 없이 내용 변경 또는 조기 종료될 수 있으며 혜택 지급일자가 변경될 수 있습니다.',
                        '불가피한 상황으로 당첨 상품 지급이 어려울 경우, 동일한 가격대의 타 상품으로 지급 됩니다.',
                        '스타벅스 기프티콘을 선택한 유저에게는 2025년 5월 23일(금) 쪽지를 통해 개별 안내될 예정이며, 당첨자와 연락이 어려운 경우 상품 지급이 불가하오니 쪽지 수신이 거부되어 있다면 해제 부탁드립니다.',
                        '이벤트 당첨자는 2025년 5월 23일(금) 지급 될 예정이며, 구매한 별풍선을 모두 선물 한 유저 대상으로 지급됩니다.',
                      ].map((text, idx) => (
                        <li key={idx}>{text}</li>
                      ))}
                    </ul>
                  </motion.div>
                </li>
                <li
                  className={`${activeGuideLines === 1 ? styles.active : ''}`}>
                  <div className={styles.title_box}>
                    <button
                      type='button'
                      onClick={handleActiveGuideLine}
                      value={1}>
                      <em>별풍선/퀵뷰/구독 자동결제</em> 이벤트 유의사항
                    </button>
                  </div>
                  <motion.div
                    className={styles.desc_box}
                    initial={{height: 0, opacity: 0}}
                    animate={{
                      height: activeGuideLines === 1 ? 'auto' : 0,
                      opacity: activeGuideLines === 1 ? 1 : 0,
                    }}
                    transition={{duration: 0.3}}
                    style={{overflow: 'hidden'}}>
                    <ul>
                      {[
                        '본 이벤트는 별풍선/퀵뷰/구독 자동결제 대상 이벤트이며, 일반결제 및 선물권의 경우 경품 대상에서 제외됩니다.',
                        '본 이벤트는 1개월 내 자동결제 이력이 없는 유저라면, 자동으로 이벤트에 참여됩니다.',
                        '이미 자동결제를 사용중인 아이템의 경우 참여 불가능하며 이벤트 결제 금액에서 제외됩니다.',
                        '아이템 취소 및 환불 시 경품 지급 대상에서 제외됩니다.',
                        '이벤트 당첨자는 2025년 5월 23일(금) 쪽지를 통해 개별 안내 될 예정이며, 당첨자와 연락이 어려운 경우 상품 지급이 불가하오니 쪽지 수신이 거부되어 있다면 해제 부탁드립니다.',
                        '불가피한 상황으로 당첨 상품 지급이 어려울 경우, 동일한 가격대의 타 상품으로 지급 됩니다.',
                        '잘못된 정보로 상품을 수령하지 못한 경우에는 재 발송이 불가합니다.',
                        '비정상적이거나 불법적인 방법으로 이벤트에 참여하신 경우 경품 지급 대상에서 제외되며, 법적인 처벌을 받을 수 있습니다.',
                        '정당한 이유 없이 이벤트 참여건에 대해 반복하여 환불을 신청할 경우, 환불이 제한 될 수 있습니다.',
                        '본 이벤트는 사정에 의하여 사전 고지 없이 내용 변경 또는 조기 종료될 수 있으며 혜택 지급 일자가 변경될 수 있습니다.',
                      ].map((text, idx) => (
                        <li key={idx}>{text}</li>
                      ))}
                    </ul>
                  </motion.div>
                </li>
              </ul>
            </div>
          </section>
        </div>

        {!!layerData.content && (
          <div
            className={`${styles.layer_modal_wrap} ${!!layerData.content ? styles.active : ''}`}>
            <div className={styles.flex_box}>
              <div className={styles.modal_contents}>
                <div className={styles.alert_layer}>
                  <div className={styles.contents}>
                    <p>{layerData.content}</p>
                  </div>
                  <div className={styles.btn_box}>
                    {layerData.handleConfirm ? (
                      <>
                        <button
                          type='button'
                          onClick={() => setLayerData(initialLayerData)}>
                          취소
                        </button>
                        <button
                          type='button'
                          onClick={() => {
                            setLayerData(initialLayerData);
                            layerData.handleConfirm();
                          }}>
                          확인
                        </button>
                      </>
                    ) : (
                      <button
                        type='button'
                        onClick={() => setLayerData(initialLayerData)}>
                        확인
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  );
};

export default AutopayBenefit;
